import React, { useEffect, useState, useRef } from 'react'
import { isNil, isEmpty } from 'ramda'
import PropTypes from 'prop-types'
import { useIntl, FormattedMessage } from 'react-intl'
import UnfoldMoreIcon from '@material-ui/icons/UnfoldMore'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  makeStyles,
} from '@material-ui/core'

import { ReactComponent as TableNotFoundIcon } from 'app/components/icons/table-not-found-icon.svg'

import Actions from 'app/main/messages/timeline/Action/Actions'
import Status from 'app/main/messages/timeline/components/Status'
import { useOpenCompose } from 'app/main/composeMessage/useOpenCompose'

import Channels from 'app/componentV4/Channel/Channels'
import Recipients from 'app/componentV4/MessageDetail/Recipients'
import TitlePreview from 'app/main/messages/timeline/components/TitlePreview'
import TableRowsLoader from 'app/main/messages/timeline/Skeleton/LoaderTableView'
import {
  DRAFT,
  SORT_STATUSES,
  SORT_CREATED_AT,
} from 'app/constants/messageDetail'
import {
  checkPoll,
  checkEvent,
  checkDraft,
  checkUnsend,
  checkMetrics,
  checkPublicLink,
} from 'commons/timelineAction'
import { ACTION_EDIT, GENERAL_DIALOG } from 'app/constants/compose'
import { getLocalizedDate } from 'commons/date'
import { getSubGroupOptions } from 'commons/groupLogic'

const useStyles = makeStyles((theme) => ({
  root: {
    height: 'fit-content',
    maxHeight: 'calc(100vh - 306px)',
    borderRadius: '8px',
    backgroundColor: theme.colors.white,
    '& .MuiTableCell-root': {
      padding: '1rem',
      borderBottom: `1px solid ${theme.colors['primary-350']}`,
    },
    '& .MuiTableCell-stickyHeader': {
      backgroundColor: theme.colors.white,
      color: theme.colors.primary,
      fontSize: '14px',
      fontWeight: 'bolder',
    },
    '& .MuiTableBody-root > .MuiTableRow-root': {
      height: '63px',
    },
  },
  channels: {
    width: '150px',
  },
}))

const TableView = (props) => {
  const {
    isLoading = true,
    isOpenDetail = false,
    onSortTable,
    onOpenDetail,
    onOpenMetric = () => {},
    notifications,
    onOpenComment = () => {},
    handleFetchTimeline,
    onSuccseAndRedirectPage,
    isFavoriteLoading,
    updateFavorites,
  } = props

  const classes = useStyles()
  const { formatMessage } = useIntl()
  const { onOpenCompose } = useOpenCompose()

  const scrollRef = useRef(null)
  const [sortDate, setSortDate] = useState('DESC')
  const [sortStatus, setSortStatus] = useState('DESC')
  const [isDialog, setIsDialog] = useState(false)
  const [hoveredItem, setHoveredItem] = useState(null)

  let isAction = {
    isDraft: false,
    isEvent: false,
    isPublicLink: false,
    isUnsend: false,
    isScheduled: false,
    isMetrics: false,
    isPoll: false,
  }

  const checkAction = (data) => {
    const isDraft = checkDraft(data.status)
    const isEvent = checkEvent(
      data.type,
      data.status,
      data?.event?.status,
      isNil(data.scheduleCanceledDate),
    )
    const isPublicLink = checkPublicLink(data.publicLink, data.status)
    const isUnsend = checkUnsend(
      data.type,
      data.status,
      data.channels,
      data?.event?.status,
    )
    const isScheduled = data.isScheduled
    const isMetrics = checkMetrics(
      data.isMetricDisabled,
      data.status,
      data.metricDeliveryPercentage,
    )
    const isPoll = checkPoll(data.poll)

    isAction = {
      isDraft: isDraft,
      isEvent: isEvent,
      isPublicLink: isPublicLink,
      isUnsend: isUnsend,
      isScheduled: isScheduled,
      isMetrics: isMetrics,
      isPoll: isPoll,
    }

    return (
      isPublicLink ||
      isUnsend ||
      isDraft ||
      isEvent ||
      isScheduled ||
      isMetrics ||
      isPoll
    )
  }

  const handleSelectRow = (row, index) => {
    setIsDialog(true)
    setHoveredItem(index)
    if (row.status === DRAFT) {
      onOpenCompose(row.id, GENERAL_DIALOG, handleFetchTimeline, ACTION_EDIT)
    } else {
      onOpenDetail(row.id)
    }
  }

  useEffect(() => {
    // delete hightlight row when close detail
    if (isOpenDetail) return
    else {
      setIsDialog(false)
      setHoveredItem(null)
    }
  }, [isOpenDetail])
  useEffect(() => {
    if (isLoading && !isNil(scrollRef.current)) {
      scrollRef.current.scrollTop = 0
    }
  }, [isLoading])

  return (
    <TableContainer className={classes.root} ref={scrollRef}>
      <Table stickyHeader aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell scope="Message title" style={{ width: '25vw' }}>
              {formatMessage({
                id: 'message.title.label',
              })}
            </TableCell>
            <TableCell scope="Channels" style={{ width: '10vw' }}>
              {formatMessage({
                id: 'channels.label',
              })}
            </TableCell>
            <TableCell scope="Recipients" style={{ width: '20vw' }}>
              {formatMessage({
                id: 'label.recipients',
              })}
            </TableCell>
            <TableCell scope="Status" style={{ width: '15vw' }}>
              {formatMessage({
                id: 'text.status',
              })}
              <IconButton
                aria-label="Sort Status"
                size="small"
                onClick={() => {
                  onSortTable(`${SORT_STATUSES}:${sortStatus}`)
                  setSortStatus((prev) => (prev === 'DESC' ? 'ASC' : 'DESC'))
                }}
              >
                <UnfoldMoreIcon fontSize="small" />
              </IconButton>
            </TableCell>
            <TableCell scope="Date" style={{ width: '30vw' }}>
              {formatMessage({
                id: 'text.date',
              })}
              <IconButton
                aria-label="Sort Date"
                size="small"
                onClick={() => {
                  onSortTable(`${SORT_CREATED_AT}:${sortDate}`)
                  setSortDate((prev) => (prev === 'DESC' ? 'ASC' : 'DESC'))
                }}
              >
                <UnfoldMoreIcon fontSize="small" />
              </IconButton>
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {isLoading ? (
            <TableRowsLoader rowsNum={10} />
          ) : (
            <>
              {isEmpty(notifications) ? (
                <TableRow>
                  <TableCell colSpan={5} className="border-0">
                    <div className="m-60">
                      <div className="mx-auto flex w-fit">
                        <TableNotFoundIcon />
                      </div>
                      <Typography className="text-16 font-bold text-neutrals-800 my-5 flex justify-center">
                        {formatMessage({
                          id: 'text.no.results',
                        })}
                      </Typography>
                      <Typography className="text-neutrals-400 my-2 flex justify-center">
                        {formatMessage({
                          id: `text.adjusting.search`,
                        })}
                      </Typography>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {notifications.map((row, index) => {
                    const formattedGroups = getSubGroupOptions(
                      formatMessage,
                      row.groups,
                    )

                    return (
                      <TableRow
                        key={`message-table-view-${row.id}`}
                        onMouseEnter={() => setHoveredItem(index)}
                        onMouseLeave={() =>
                          isDialog ? null : setHoveredItem(null)
                        }
                        className={
                          hoveredItem === index
                            ? 'bg-primary-200 cursor-pointer'
                            : ''
                        }
                      >
                        <TableCell
                          id="column-message-title"
                          onClick={() => handleSelectRow(row, index)}
                          style={{ minWidth: '180px', width: '25vw' }}
                        >
                          <TitlePreview data={row} />
                        </TableCell>
                        <TableCell
                          id="column-channels"
                          onClick={() => handleSelectRow(row, index)}
                          style={{ width: '10vw' }}
                        >
                          <Channels
                            channels={row.channels}
                            isTooltip={true}
                            className={classes.channels}
                          />
                        </TableCell>
                        <TableCell
                          id="column-recipients"
                          onClick={() => handleSelectRow(row, index)}
                          style={{ width: '20vw' }}
                        >
                          <Recipients
                            groups={formattedGroups}
                            recipients={row.recipients}
                            channels={row.channels}
                            isDraft={checkDraft(row.status)}
                            isScheduled={row.isScheduled}
                            isTooltip={true}
                          />
                        </TableCell>
                        <TableCell
                          id="column-status"
                          onClick={() => handleSelectRow(row, index)}
                          style={{ width: '15vw' }}
                        >
                          <Status status={row.status} />
                        </TableCell>
                        <TableCell
                          id="column-date-action"
                          style={{ width: '30vw' }}
                        >
                          {hoveredItem === index && checkAction(row) ? (
                            <Actions
                              id={row.id}
                              isFavorite={row.isFavorited || false}
                              isDisplayFavorite={true}
                              isPoll={isAction?.isPoll}
                              isEvent={isAction?.isEvent}
                              countComment={row.comments}
                              isDraft={isAction?.isDraft}
                              isUnsend={isAction?.isUnsend}
                              isMetrics={isAction?.isMetrics}
                              isScheduled={isAction?.isScheduled}
                              isSharingEnabled={row.isSharingEnabled}
                              isPublicLink={isAction?.isPublicLink}
                              isDialog={(value) => setIsDialog(value)}
                              onOpenMetric={() => onOpenMetric(row.id)}
                              onOpenComment={() => onOpenComment(row.id)}
                              scheduleCanceledDate={row.scheduleCanceledDate}
                              handleFetchTimeline={handleFetchTimeline}
                              onSuccseAndRedirectPage={onSuccseAndRedirectPage}
                              isReminder={
                                row.isReminder && !isNil(row.reminder)
                              }
                              isFavoriteLoading={isFavoriteLoading}
                              updateFavorites={updateFavorites}
                            />
                          ) : (
                            <div>
                              <Typography className="whitespace-nowrap text-16 text-neutrals-600 font-normal">
                                {getLocalizedDate(row.createdAt, 'DATE_TIME')}
                              </Typography>
                              <Typography className="whitespace-nowrap text-12 text-grey-200 font-normal">
                                <FormattedMessage
                                  id="created.by.label"
                                  values={{
                                    fullName: (
                                      <span
                                        className={
                                          !isNil(row.creator.deletedAt)
                                            ? 'line-through'
                                            : ''
                                        }
                                      >
                                        {`${row.creator.firstName} ${row.creator.lastName}`}
                                      </span>
                                    ),
                                  }}
                                />
                              </Typography>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </>
              )}
            </>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

const dataPropTypes = {
  id: PropTypes.number,
  type: PropTypes.number,
  scope: PropTypes.number,
  level: PropTypes.number,
  isScheduled: PropTypes.bool,
  isDraft: PropTypes.bool,
  isCanceled: PropTypes.bool,
  isReminder: PropTypes.bool,
  isSharingEnabled: PropTypes.bool,
  isMetricDisabled: PropTypes.bool,
  metricDeliveryPercentage: PropTypes.number,
  timezone: PropTypes.string,
  subUserId: PropTypes.number,
  channels: PropTypes.arrayOf(PropTypes.number),
  translation: PropTypes.shape({
    title: PropTypes.string,
    descriptionPreview: PropTypes.string,
    body: PropTypes.string,
    languageId: PropTypes.number,
    languageCode: PropTypes.string,
  }),
  audioUrl: PropTypes.string,
  publicLink: PropTypes.string,
  status: PropTypes.string,
  recipients: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    }),
  ),
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
  ),
  sendDate: PropTypes.string,
  unsendDate: PropTypes.string,
  scheduleCanceledDate: PropTypes.string,
  expirationDate: PropTypes.string,
  canceledDate: PropTypes.string,
  defaultImageUrl: PropTypes.string,
  defaultImageType: PropTypes.string,
  event: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    startTimeZone: PropTypes.string,
    endTimeZone: PropTypes.string,
    status: PropTypes.number,
  }),
  createdAt: PropTypes.string,
  createdBy: PropTypes.string,
  updatedAt: PropTypes.string,
  creator: PropTypes.shape({
    id: PropTypes.number,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    deletedAt: PropTypes.string,
  }),
  poll: PropTypes.shape({
    id: PropTypes.number,
    status: PropTypes.string,
  }),
}
TableView.propTypes = {
  isLoading: PropTypes.bool,
  recipients: PropTypes.object,
  isOpenDetail: PropTypes.bool,
  isFavoriteLoading: PropTypes.bool,
  onSortTable: PropTypes.func,
  onOpenDetail: PropTypes.func,
  onOpenMetric: PropTypes.func,
  onOpenComment: PropTypes.func,
  handleFetchTimeline: PropTypes.func,
  onSuccseAndRedirectPage: PropTypes.func,
  updateFavorites: PropTypes.func,
  notifications: PropTypes.arrayOf(PropTypes.shape(dataPropTypes)).isRequired,
}

export default TableView
